/* eslint-disable no-nested-ternary */
import { get } from 'lodash';
import moment from 'moment';
import config from '../config';
import { types as sdkTypes } from './sdkLoader';
import { flexiblePricingConfig, HUNT_ACTIVITY } from '../marketplace-custom-config';
import { LODGING_INFO } from '../shared/config';

const { Money } = sdkTypes;

export const PUBLISHED_TAB = 'PUBLISHED_TAB';
export const UNPUBLISHED_TAB = 'UNPUBLISHED_TAB';

export { LODGING_INFO };

export const LODGING_TYPE = {
  CABIN: 'cabin',
  HOUSE: 'house',
};

/**
 * Helper function to check if flexible pricing should be enabled
 * Requires both the feature flag to be enabled AND the package activity to be HUNT_ACTIVITY
 * @param {string} packageActivity - The activity type of the package
 * @returns {boolean} True if flexible pricing should be enabled
 */
export const isFlexiblePricingEnabled = packageActivity => {
  return flexiblePricingConfig.enabled && packageActivity === HUNT_ACTIVITY;
};

export const getInitialPackage = (values, activity) => {
  const minPrice = new Money(config.listingPackageMinimumPrice, config.currency);
  const availableDateFrom = values?.availableDateFrom;
  const availableDateTo = values?.availableDateTo;
  let dates = null;

  if (availableDateFrom && availableDateTo) {
    dates = {
      startDate: moment(availableDateFrom).toDate(),
      endDate: moment(availableDateTo).toDate(),
    };
  }

  const defaultFlexiblePricing = {
    enabled: isFlexiblePricingEnabled(activity),
    ...flexiblePricingConfig.defaultValues,
  };

  const storedFlexiblePricing = values?.flexiblePricing || {};
  const mergeFlexiblePricing = () => {
    const categories = ['adultHunter', 'adultNonHunter', 'youthHunter', 'youthNonHunter'];
    const result = { ...defaultFlexiblePricing };
    if (typeof storedFlexiblePricing.enabled === 'boolean') {
      result.enabled = storedFlexiblePricing.enabled;
    }

    categories.forEach(category => {
      const base = defaultFlexiblePricing[category] || {};
      const override = storedFlexiblePricing[category] || {};
      result[category] = {
        ...base,
        ...override,
      };
    });

    return result;
  };

  const flexiblePricing = mergeFlexiblePricing();

  return {
    id: values ? values.id : '',
    title: values ? values.title : '',
    description: values ? values.description : '',
    images: values?.images ? values.images : [],
    days: values?.days || {
      min: 1,
      max: 1,
    },
    guests: values?.guests || {
      min: 1,
      max: 1,
    },
    price: values ? values.price : minPrice,
    isPublished: values ? values.isPublished : true,
    hasLodging: values ? values.hasLodging : 'Not Offered',
    activity,
    activities: values && values.activities ? values.activities : [],
    activitiesInfo: values ? values.activitiesInfo : null,
    species: values ? values.species : [],
    subSpecies: values ? values.subSpecies : null,
    lodgingPrices:
      values && values.lodgingPrices
        ? values.lodgingPrices.map(lodgingPrice => lodgingPrice.option)
        : [],
    species_unit: values?.species_unit ?? null,
    methods: values?.methods || {
      allowsArchery: values?.allowsArchery || false,
      allowsFirearms: values?.allowsFirearms || false,
      showDatesField: values?.showDatesField || false,
      archeryInfo: values?.archeryInfo || null,
      firearmsInfo: values?.firearmsInfo || null,
    },
    hasKidsFriendlyExperience: values?.hasKidsFriendlyExperience || false,
    kidsFriendlyInfo: values?.hasKidsFriendlyExperience ? values?.kidsFriendlyInfo : null,
    interactiveExperience: values?.interactiveExperience || 'N/A',
    interactiveExperienceInfo: values?.interactiveExperienceInfo || null,
    catchAndRelease: values?.catchAndRelease || false,
    fishTypes: values?.fishTypes || [],
    dates,
    hasQuantityRestricted: values?.hasQuantityRestricted || false,
    quantity: values?.quantity || {
      booked: 0,
      total: 0,
    },
    flexiblePricing,
  };
};

export const getStartingPrice = packageItem => {
  const { price, guests, days, includedLodging = [], flexiblePricing } = packageItem || {};

  const parsedPrice = price ? (typeof price === 'string' ? JSON.parse(price) : price) : null;
  let priceAmount = parsedPrice ? parseInt(parsedPrice.amount, 10) : 0;

  let minGuests = guests?.min;
  const maxGuests = guests?.max;
  const minDays = days?.min || 1;

  // Handle special case for flexible pricing when max is 0
  // Apply youth hunter discount to the base price
  if (maxGuests === 0 && flexiblePricing) {
    const youthHunterDiscount =
      flexiblePricing?.youthHunter?.discount ??
      flexiblePricingConfig.defaultValues.youthHunter.discount;
    const discountAmount = (priceAmount * youthHunterDiscount) / 100;
    priceAmount -= discountAmount;
  }

  // Handle special case: if min is 0, use 1 as the effective minimum for calculation
  // unless max is also 0
  if (minGuests === 0) {
    if (maxGuests === 0) {
      // Both min and max are 0, use 1 for calculation
      minGuests = 1;
    } else {
      // Min is 0 but max > 0, use 1 as the lowest effective value
      minGuests = 1;
    }
  } else if (minGuests === undefined || minGuests === null) {
    minGuests = 1;
  }

  let startingPrice = '';

  if (includedLodging.length) {
    const lodgingPriceAmount = includedLodging.reduce((acc, curr) => {
      if (curr.price) {
        const lodgingPrice = JSON.parse(curr.price, sdkTypes.reviver);
        return acc + lodgingPrice.amount;
      }
      return acc;
    }, 0);

    // 💬 Lodging cost spread across guests
    const lodgingFeeTotal = lodgingPriceAmount * minDays;
    const lodgingFeePerGuest = lodgingFeeTotal / minGuests;

    // 💬 Total per guest: daily rate + share of lodging
    const totalPricePerGuest = priceAmount * minDays + lodgingFeePerGuest;

    startingPrice = new Money(totalPricePerGuest, config.currency);
  } else {
    // 💬 Only per-day price × minimum days
    startingPrice = new Money(priceAmount * minDays, config.currency);
  }

  return startingPrice;
};

export const getTransactionFromInquiry = (user, packageId) => {
  const pendingInquiries = get(user, 'attributes.profile.metadata.pendingInquiries.items', null);

  if (pendingInquiries && pendingInquiries[packageId]) {
    const inquiry = pendingInquiries[packageId];

    const now = moment();
    const startDateObj = moment(inquiry.startDate);

    if (moment(now).isBefore(startDateObj)) {
      return pendingInquiries[packageId]?.transactionId;
    }
  }

  return null;
};

export const checkPackageAvailability = (packageItem, currentTime) => {
  const { availableDateFrom, availableDateTo, quantity, hasQuantityRestricted } = packageItem || {};

  // Convert date strings to Moment objects
  const from = availableDateFrom ? moment(availableDateFrom) : null;
  const to = availableDateTo ? moment(availableDateTo) : null;

  // Get current date and time
  const now = moment(currentTime);

  // Initialize booking range variables
  let bookingRangeIsFuture = false;
  let bookingRangeIsPast = false;
  let bookingRangePresent = false;

  // Check if booking range is currently active
  if (from && to && now.isBetween(from, to, 'day', '[]')) {
    bookingRangePresent = true;
  }

  // Check if booking range is in the future
  else if (to && now.isBefore(to, 'day')) {
    bookingRangeIsFuture = true;
  }

  // Check if booking range is in the past
  else if (from && now.isAfter(from, 'day')) {
    bookingRangeIsPast = true;
  }

  const quantityBooked = quantity?.booked ? quantity.booked : 0;
  const quantityTotal = quantity?.total ? quantity.total : 0;
  const quantityAvailable = quantityTotal - quantityBooked;

  return {
    bookingRangeIsFuture,
    bookingRangeIsPast,
    bookingRangePresent,
    isAvailable: hasQuantityRestricted ? quantityAvailable > 0 : true,
  };
};

export const isPackageOnWaitingList = (user, packageId) => {
  const waitingList = get(user, 'attributes.profile.publicData.waitingList', []);

  return waitingList.some(item => item.packageId === packageId);
};

export const getRVDetails = (listing, packageInfo) => {
  const campsites = get(listing, 'attributes.publicData.campsites', []);
  const lodgingPrices = get(packageInfo, 'lodgingPrices', []);
  const isRvFriendly = campsites.some(campsite => campsite.allowsRvs);

  const requiredRV = lodgingPrices.filter(
    lodgingPrice => lodgingPrice.type === 'rv' && lodgingPrice.option === LODGING_INFO.REQUIRED
  );

  // Check if package has rv sites
  const hasRVSites = campsites.some((campsite, index) => {
    const hasRequiredRv = requiredRV.some(rv => {
      if (index === rv.index && campsite.rv.hasCampsite) {
        return true;
      }

      return false;
    });

    return hasRequiredRv;
  });

  return {
    isRvFriendly,
    hasRVSites,
  };
};

const findLodgingPriceOption = (lodgingPricesOptions, item) =>
  lodgingPricesOptions.find(
    lodgingPrice => lodgingPrice.type === item.type && lodgingPrice.index === item.index
  );

export const getPackageLodgingPrices = (packageInfo, lodgingPricesOptions) => {
  const baseLodgingPrices = get(packageInfo, 'lodgingPrices', []);

  const { optionalLodging, includedLodging } = baseLodgingPrices.reduce(
    (acc, item) => {
      const lodgingPriceOption = findLodgingPriceOption(lodgingPricesOptions, item);

      if (!lodgingPriceOption) return acc;

      const newItem = { ...item, ...lodgingPriceOption };
      if (newItem.option === LODGING_INFO.OPTIONAL) acc.optionalLodging.push(newItem);
      if (newItem.option === LODGING_INFO.REQUIRED) acc.includedLodging.push(newItem);

      return acc;
    },
    { optionalLodging: [], includedLodging: [] }
  );

  const includedLodgingPrice = includedLodging.reduce((acc, item) => {
    if (item.price) {
      return acc + JSON.parse(item.price, sdkTypes.reviver).amount;
    }
    return acc;
  }, 0);

  return {
    optionalLodging,
    includedLodging,
    includedLodgingPrice,
  };
};

export const hasLodgingForPackage = (packageInfo, includeOptional = true) => {
  const { lodgingPrices = [] } = packageInfo || {};

  const options = includeOptional
    ? [LODGING_INFO.REQUIRED, LODGING_INFO.OPTIONAL]
    : [LODGING_INFO.REQUIRED];

  return lodgingPrices.some(
    lodgingPrice =>
      [LODGING_TYPE.HOUSE, LODGING_TYPE.CABIN].includes(lodgingPrice.type) &&
      options.includes(lodgingPrice.option)
  );
};

export const getEarlyAccessExpirationDate = (timestamp, earlyAccessDays, availableDateFrom) => {
  let baseTimestamp = timestamp;

  // If availableDateFrom is provided and is later than timestamp, use it as the base
  if (availableDateFrom) {
    const availableDateFromTimestamp = moment
      .utc(availableDateFrom, 'YYYY-MM-DD')
      .startOf('day')
      .unix();

    if (availableDateFromTimestamp > timestamp) {
      baseTimestamp = availableDateFromTimestamp;
    }
  }

  // Calculate the expiration date based on the base timestamp
  const expirationDate = moment.unix(baseTimestamp).add(earlyAccessDays, 'days');

  return expirationDate.unix();
};

export const isEarlyAccessAvailable = timestamp => {
  const currentTimestamp = Math.floor(Date.now() / 1000);

  return config.packagesEarlyAccessDays > 0 && timestamp > 0 && timestamp > currentTimestamp;
};

export const syncEarlyAccessExpiration = (packages = []) => {
  return packages.map(item => {
    const { availableDateFrom } = item;

    const timestamp = moment.utc().unix();
    const expiration = getEarlyAccessExpirationDate(
      timestamp,
      config.packagesEarlyAccessDays,
      availableDateFrom
    );

    return {
      ...item,
      earlyAccessExpiration: expiration,
    };
  });
};

/**
 * Handles the logic for adding/removing lodging add-ons
 * and potentially adjusting dates when lodging is added or removed
 *
 * @param {number} index - The index of the lodging add-on being toggled
 * @param {Array} currentLodgingAddons - The current list of selected lodging add-on indices
 * @param {Object} dateValues - The current date values object containing startDate, endDate, etc.
 * @param {Array} availableDays - Array of available days in YYYY-MM-DD format
 * @param {boolean} hasLodging - Whether the package has included lodging
 * @param {Function} updateForm - Function to update form values (usually form.change)
 * @returns {Array} The updated lodging add-ons array after the change
 */
export const handleLodgingAddOnChange = (
  index,
  currentLodgingAddons = [],
  dateValues = {},
  availableDays = [],
  hasLodging = false,
  updateForm
) => {
  // Toggle the lodging add-on
  const updatedLodgingAddons = currentLodgingAddons.includes(index)
    ? currentLodgingAddons.filter(addon => addon !== index)
    : [...currentLodgingAddons, index];

  // Get relevant date values (dateValues may be null when no dates are selected yet)
  const { startDate, startLodgingDate } = dateValues || {};

  if (!startDate) {
    return updatedLodgingAddons;
  }

  // If we're adding lodging and don't have a startLodgingDate yet
  if (updatedLodgingAddons.length > 0 && !startLodgingDate) {
    const startLodgingDateValue = moment(startDate).subtract(1, 'day');
    const isPreviousDayBlocked = !availableDays.includes(
      startLodgingDateValue.format('YYYY-MM-DD')
    );

    if (!isPreviousDayBlocked && updateForm) {
      updateForm('dates', {
        ...dateValues,
        startDate: startLodgingDateValue.toDate(),
        startLodgingDate: startLodgingDateValue.toDate(),
      });

      updateForm('bookingDates', {
        ...dateValues,
        startDate: startLodgingDateValue.toDate(),
        startLodgingDate: startLodgingDateValue.toDate(),
      });
    }
  }
  // If we're removing all optional lodging and the package doesn't have included lodging
  else if (updatedLodgingAddons.length === 0 && startLodgingDate && !hasLodging) {
    const startLodgingDateValue = moment(startDate).add(1, 'day');
    const isNextDayBlocked = !availableDays.includes(startLodgingDateValue.format('YYYY-MM-DD'));

    if (!isNextDayBlocked && updateForm) {
      updateForm('dates', {
        ...dateValues,
        startLodgingDate: null,
        startDate: startLodgingDateValue.toDate(),
      });

      updateForm('bookingDates', {
        ...dateValues,
        startLodgingDate: null,
        startDate: startLodgingDateValue.toDate(),
      });
    }
  }

  return updatedLodgingAddons;
};

export const getFlexiblePricingData = (presetFlexiblePricing = {}, minGuestSize = 1) => {
  const adultHunterQuantity = presetFlexiblePricing?.adultHunter?.quantity || minGuestSize || 1;

  return {
    adultHunter: {
      ...flexiblePricingConfig.defaultValues.adultHunter,
      discount:
        presetFlexiblePricing.adultHunter?.discount ??
        flexiblePricingConfig.defaultValues.adultHunter.discount,
      quantity: adultHunterQuantity,
    },
    adultNonHunter: {
      ...flexiblePricingConfig.defaultValues.adultNonHunter,
      ...presetFlexiblePricing.adultNonHunter,
    },
    youthHunter: {
      ...flexiblePricingConfig.defaultValues.youthHunter,
      ...presetFlexiblePricing.youthHunter,
    },
    youthNonHunter: {
      ...flexiblePricingConfig.defaultValues.youthNonHunter,
      ...presetFlexiblePricing.youthNonHunter,
    },
  };
};

/**
 * Calculate flexible pricing for selected party data
 * @param {Object} selectedData - Object containing party counts for each category
 * @param {number} selectedData.adultHunter - Number of adult hunters
 * @param {number} selectedData.adultNonHunter - Number of adult non-hunters
 * @param {number} selectedData.youthHunter - Number of youth hunters
 * @param {number} selectedData.youthNonHunter - Number of youth non-hunters
 * @param {Object|string} packageData - Package object or package ID
 * @returns {Object} Object with price in pence for each category
 */
export const calculateFlexiblePricing = (selectedData, packageData, activityDays) => {
  const parsedPrice =
    typeof packageData.price === 'string' ? JSON.parse(packageData.price) : packageData.price;

  const mergedConfig = getFlexiblePricingData(packageData.flexiblePricing);

  // Calculate price for each category
  const calculateCategoryPrice = (count, discount) => {
    if (count === 0) {
      return {
        unitPrice: new Money(0, parsedPrice.currency),
        totalPrice: new Money(0, parsedPrice.currency),
      };
    }

    const quantity = count * activityDays;

    const discountedAmount = Math.round((parsedPrice.amount * discount) / 100);
    const discountedPrice = parsedPrice.amount - discountedAmount;
    const unitPrice = new Money(discountedPrice, parsedPrice.currency);
    const totalPrice = new Money(discountedPrice * quantity, parsedPrice.currency);

    return {
      unitPrice,
      totalPrice,
    };
  };

  return {
    adultHunter: calculateCategoryPrice(
      selectedData.adultHunter || 0,
      mergedConfig.adultHunter?.discount
    ),
    adultNonHunter: calculateCategoryPrice(
      selectedData.adultNonHunter || 0,
      mergedConfig.adultNonHunter?.discount
    ),
    youthHunter: calculateCategoryPrice(
      selectedData.youthHunter || 0,
      mergedConfig.youthHunter?.discount
    ),
    youthNonHunter: calculateCategoryPrice(
      selectedData.youthNonHunter || 0,
      mergedConfig.youthNonHunter?.discount
    ),
  };
};

export const formatPricingFormValues = (
  values,
  packageMinParty,
  listing,
  hasLodging,
  currentUser
) => {
  const selectedPartySize = values && values.partySize ? values.partySize : packageMinParty;

  // Handle both standard party size and flexible pricing data
  let partySize = packageMinParty;
  let flexibleGuestSize = null;

  if (selectedPartySize) {
    if (typeof selectedPartySize === 'object' && selectedPartySize.partySize) {
      // Flexible pricing data
      partySize = parseInt(selectedPartySize.partySize, 10);
      flexibleGuestSize = selectedPartySize.flexibleGuestSize;
    } else {
      // Standard party size
      partySize = parseInt(selectedPartySize, 10);
    }
  }

  const formattedValues = {
    bookingDates: values.dates,
    party: partySize,
    flexibleGuestSize,
    lodgingAddons: values.lodgingAddons,
  };

  const { bookingDates, ...bookingData } = formattedValues;

  const initialValues = {
    listing,
    bookingData,
    includeLodging: values?.lodgingAddons?.length > 0 || hasLodging,
    bookingDates: {
      bookingStart: bookingDates.startDate,
      bookingEnd: bookingDates.endDate,
      startLodgingDate: bookingDates.startLodgingDate,
    },
    confirmPaymentError: null,
  };

  const saveToSessionStorage = !currentUser;

  return {
    initialValues,
    saveToSessionStorage,
  };
};
